import React, { useState, useEffect, useCallback, useMemo } from "react";  
import { useNavigate, Link } from "react-router-dom";  
import Navbar from "./Navbar";  
import api from "../api";  
import {  
  AreaChart, Area, PieChart, Pie, Cell,  
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,  
} from "recharts";  
import { motion, AnimatePresence } from "framer-motion";  
  
/* ────────────────────────────────────────────────── */  
/* Recharts custom tooltip                             */  
/* ────────────────────────────────────────────────── */  
const ChartTooltip = ({ active, payload, label }) => {  
  if (!active || !payload?.length) return null;  
  return (  
    <div className="bg-black/95 border border-gold/40 px-3 py-2 rounded-lg shadow-2xl backdrop-blur-sm">  
      <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{label}</p>  
      {payload.map((entry, i) => (  
        <p key={i} className="text-sm font-semibold" style={{ color: entry.color }}>  
          {typeof entry.value === "number" ? `$${entry.value.toFixed(2)}` : entry.value}  
        </p>  
      ))}  
    </div>  
  );  
};  
  
/* ────────────────────────────────────────────────── */  
/* Main Dashboard                                      */  
/* ────────────────────────────────────────────────── */  
function Dashboard() {  
  const navigate = useNavigate();  
  const [loading, setLoading] = useState(true);  
  const [notification, setNotification] = useState(null);  
  
  const [botMeta, setBotMeta] = useState({ hasBinance: false, hasIG: false });  
  const [summary, setSummary] = useState(null);  
  const [closedTrades, setClosedTrades] = useState([]);  
  const [assets, setAssets] = useState([]);  
  
  const showNotification = (message, type = "info") => {  
    setNotification({ message, type });  
    setTimeout(() => setNotification(null), 4000);  
  };  
  
  const fetchAll = useCallback(async () => {  
    try {  
      const [statusRes, igRes, logsRes, assetsRes] = await Promise.all([  
        api.get("/config/status").catch(() => ({ data: {} })),  
        api.get("/users/ig/status").catch(() => ({ data: {} })),  
        api.get("/logs/trade_logs", { params: { limit: 10000 } }).catch(() => ({ data: {} })),  
        api.get("/assets/available").catch(() => ({ data: [] })),  
      ]);  
  
      setBotMeta({  
        hasBinance: !!statusRes.data.has_binance_keys,  
        hasIG: !!igRes.data.connected,  
      });  
      setSummary(logsRes.data.summary || null);  
      setClosedTrades(logsRes.data.closed_trades || logsRes.data.closed || []);  
      setAssets(assetsRes.data || []);  
    } catch (err) {  
      console.error("[Dashboard] fetch error:", err);  
      if (err.response?.status === 401) {  
        showNotification("Session expired", "error");  
        setTimeout(() => navigate("/login"), 1500);  
      }  
    } finally {  
      setLoading(false);  
    }  
  }, [navigate]);  
  
  useEffect(() => {  
    if (!localStorage.getItem("access_token")) return navigate("/login");  
    fetchAll();  
    const iv = setInterval(fetchAll, 30000);  
    return () => clearInterval(iv);  
  }, [fetchAll, navigate]);  
  
  /* ── Derived data ── */  
  const activeAssets = useMemo(() => assets.filter(a => a.is_active), [assets]);  
  const hasAnyKeys = botMeta.hasBinance || botMeta.hasIG;  
  const isLive = activeAssets.length > 0;  
  
  const equityData = useMemo(() => buildEquityCurve(closedTrades), [closedTrades]);  
  const winLoss = useMemo(() => {  
    const wins = closedTrades.filter(t => (t.pnl || 0) > 0).length;  
    const losses = closedTrades.filter(t => (t.pnl || 0) < 0).length;  
    return { wins, losses };  
  }, [closedTrades]);  
  
  const recentTrades = closedTrades.slice(0, 5);  
  const totalPnl = summary?.total_pnl || 0;  
  const winRate = summary?.win_rate || 0;  
  const totalTrades = summary?.total_trades || closedTrades.length;  
  
  // Weekly delta (approx: last 7d of trades)  
  const weeklyPnl = useMemo(() => {  
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;  
    return closedTrades  
      .filter(t => new Date(t.timestamp).getTime() > cutoff)  
      .reduce((s, t) => s + (t.pnl || 0), 0);  
  }, [closedTrades]);  
  
  if (loading) {  
    return (  
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">  
        <Navbar />  
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">  
          <div className="text-center">  
            <div className="relative w-14 h-14 mx-auto mb-4">  
              <div className="absolute inset-0 border-4 border-gold/20 rounded-full" />  
              <div className="absolute inset-0 border-4 border-gold border-t-transparent rounded-full animate-spin" />  
            </div>  
            <p className="text-gold text-sm font-semibold">Loading your dashboard…</p>  
          </div>  
        </div>  
      </div>  
    );  
  }  
  
  return (  
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white">  
      <Navbar />  
  
      <AnimatePresence>  
        {notification && (  
          <motion.div  
            initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}  
            className="fixed top-20 left-4 right-4 sm:left-auto sm:right-6 z-50 px-5 py-3.5 rounded-xl shadow-2xl border backdrop-blur-md sm:max-w-md bg-red-900/90 border-red-500/60"  
          >  
            <p className="text-white text-sm font-medium">{notification.message}</p>  
          </motion.div>  
        )}  
      </AnimatePresence>  
  
      <div className="max-w-6xl mx-auto py-6 sm:py-10 px-4 sm:px-6 lg:px-8 space-y-6">  
  
        {/* ─── Status Strip ─── */}  
        <motion.div  
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}  
          className="flex items-center justify-between gap-4 flex-col sm:flex-row"  
        >  
          <div className="flex items-center gap-3">  
            <div className="w-1 h-10 bg-gradient-to-b from-gold to-yellow-600 rounded-full" />  
            <div>  
              <h1 className="text-xl sm:text-2xl font-bold text-white leading-tight">Dashboard</h1>  
              <p className="text-xs text-gray-500 mt-0.5">Your trading performance at a glance</p>  
            </div>  
          </div>  
  
          <div className="flex items-center gap-3">  
            <div className={`flex items-center gap-2 px-3.5 py-2 rounded-full border backdrop-blur-sm ${  
              isLive  
                ? "bg-green-500/10 border-green-500/40"  
                : "bg-white/[0.03] border-gray-800"  
            }`}>  
              <span className={`w-2 h-2 rounded-full ${isLive ? "bg-green-400 animate-pulse" : "bg-gray-600"}`} />  
              <span className="text-xs font-semibold text-gray-200">  
                {isLive ? `Bot Live · ${activeAssets.length} active` : "Bot Standby"}  
              </span>  
            </div>  
            <Link  
              to="/my-models"  
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gold/10 border border-gold/40 hover:bg-gold/20 text-gold text-xs font-semibold transition-all"  
            >  
              Manage models  
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">  
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />  
              </svg>  
            </Link>  
          </div>  
        </motion.div>  
  
        {/* ─── Onboarding OR Dashboard content ─── */}  
        {!hasAnyKeys && closedTrades.length === 0 ? (  
          <OnboardingCard botMeta={botMeta} navigate={navigate} />  
        ) : (  
          <>  
            {/* ─── Hero KPI ─── */}  
            <motion.div  
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}  
              className="bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60 backdrop-blur-xl border border-gray-800 rounded-2xl p-6 sm:p-8"  
            >  
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">  
                {/* Hero P&L */}  
                <div className="lg:col-span-1 pb-6 lg:pb-0 lg:pr-8 lg:border-r border-b lg:border-b-0 border-gray-800">  
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">Total P&L</p>  
                  <p className={`text-4xl sm:text-5xl font-extrabold tracking-tight ${totalPnl >= 0 ? "text-green-400" : "text-red-400"}`}>  
                    {totalPnl >= 0 ? "+" : "−"}${Math.abs(totalPnl).toFixed(2)}  
                  </p>  
                  {closedTrades.length > 0 && (  
                    <div className={`flex items-center gap-1 mt-3 text-xs font-semibold ${weeklyPnl >= 0 ? "text-green-400" : "text-red-400"}`}>  
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">  
                        <path fillRule="evenodd" d={weeklyPnl >= 0  
                          ? "M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z"  
                          : "M12 13a1 1 0 100 2h5a1 1 0 001-1V9a1 1 0 10-2 0v2.586l-4.293-4.293a1 1 0 00-1.414 0L8 9.586 3.707 5.293a1 1 0 00-1.414 1.414l5 5a1 1 0 001.414 0L11 9.414 14.586 13H12z"  
                        } clipRule="evenodd" />  
                      </svg>  
                      <span>{weeklyPnl >= 0 ? "+" : "−"}${Math.abs(weeklyPnl).toFixed(2)} this week</span>  
                    </div>  
                  )}  
                </div>  
  
                {/* Supporting KPIs */}  
                <div className="lg:col-span-3 grid grid-cols-3 gap-4 sm:gap-6">  
                  <KPIStat label="Win Rate" value={`${winRate}%`} sub={`${winLoss.wins}W / ${winLoss.losses}L`} />  
                  <KPIStat label="Trades" value={totalTrades} sub={closedTrades.length === 0 ? "—" : "closed"} />  
                  <KPIStat label="Active Models" value={`${activeAssets.length}`} sub={`of ${assets.length}`} />  
                </div>  
              </div>  
            </motion.div>  
  
            {/* ─── Equity Curve ─── */}  
            {equityData.length > 0 && (  
              <motion.div  
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}  
                className="bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60 backdrop-blur-xl border border-gray-800 rounded-2xl p-6"  
              >  
                <div className="flex items-center justify-between mb-5">  
                  <div>  
                    <h3 className="text-sm font-bold text-white">Equity curve</h3>  
                    <p className="text-[11px] text-gray-500 mt-0.5">Your account balance over time</p>  
                  </div>  
                </div>  
                <ResponsiveContainer width="100%" height={280}>  
                  <AreaChart data={equityData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>  
                    <defs>  
                      <linearGradient id="equityGradient" x1="0" y1="0" x2="0" y2="1">  
                        <stop offset="5%" stopColor="#d4af37" stopOpacity={0.4} />  
                        <stop offset="95%" stopColor="#d4af37" stopOpacity={0} />  
                      </linearGradient>  
                    </defs>  
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />  
                    <XAxis dataKey="date" stroke="#4b5563" style={{ fontSize: "10px" }} tickLine={false} axisLine={false} />  
                    <YAxis stroke="#4b5563" style={{ fontSize: "10px" }} tickLine={false} axisLine={false} width={45} />  
                    <Tooltip content={<ChartTooltip />} />  
                    <Area type="monotone" dataKey="balance" stroke="#d4af37" strokeWidth={2} fill="url(#equityGradient)" />  
                  </AreaChart>  
                </ResponsiveContainer>  
              </motion.div>  
            )}  
  
            {/* ─── Two-column: Models + Recent Trades ─── */}  
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">  
              <motion.div  
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}  
                className="bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60 backdrop-blur-xl border border-gray-800 rounded-2xl p-6"  
              >  
                <div className="flex items-center justify-between mb-4">  
                  <h3 className="text-sm font-bold text-white">Your models</h3>  
                  <Link to="/my-models" className="text-[11px] font-semibold text-gold hover:text-yellow-300">Manage →</Link>  
                </div>  
                {assets.length === 0 ? (  
                  <EmptyBlock text="No models available yet" />  
                ) : (  
                  <ul className="space-y-2">  
                    {assets.map(a => (  
                      <ModelRow key={a.registry_id} asset={a} />  
                    ))}  
                  </ul>  
                )}  
              </motion.div>  
  
              <motion.div  
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}  
                className="bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60 backdrop-blur-xl border border-gray-800 rounded-2xl p-6"  
              >  
                <div className="flex items-center justify-between mb-4">  
                  <h3 className="text-sm font-bold text-white">Recent trades</h3>  
                  <Link to="/trade-logs" className="text-[11px] font-semibold text-gold hover:text-yellow-300">View all →</Link>  
                </div>  
                {recentTrades.length === 0 ? (  
                  <EmptyBlock text="No trades yet — start a model to begin" />  
                ) : (  
                  <ul className="space-y-2">  
                    {recentTrades.map((t, i) => (  
                      <TradeRow key={t.id || i} trade={t} onClick={() => navigate("/trade-logs", { state: { highlightTrade: t.id } })} />  
                    ))}  
                  </ul>  
                )}  
              </motion.div>  
            </div>  
  
            {/* ─── Win/Loss donut ─── */}  
            {closedTrades.length > 0 && (  
              <motion.div  
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}  
                className="bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60 backdrop-blur-xl border border-gray-800 rounded-2xl p-6"  
              >  
                <h3 className="text-sm font-bold text-white mb-4">Win / Loss ratio</h3>  
                <div className="flex items-center gap-8 flex-col sm:flex-row">  
                  <div className="w-full sm:w-56 h-56">  
                    <ResponsiveContainer>  
                      <PieChart>  
                        <Pie  
                          data={[  
                            { name: "Wins", value: winLoss.wins, color: "#10b981" },  
                            { name: "Losses", value: winLoss.losses, color: "#ef4444" },  
                          ]}  
                          cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value"  
                        >  
                          {["#10b981", "#ef4444"].map((c, i) => <Cell key={i} fill={c} />)}  
                        </Pie>  
                        <Tooltip content={<ChartTooltip />} />  
                      </PieChart>  
                    </ResponsiveContainer>  
                  </div>  
                  <div className="flex-1 space-y-3 w-full">  
                    <RatioRow color="bg-green-400" label="Winning trades" value={winLoss.wins} pct={totalTrades ? (winLoss.wins / totalTrades) * 100 : 0} />  
                    <RatioRow color="bg-red-400" label="Losing trades" value={winLoss.losses} pct={totalTrades ? (winLoss.losses / totalTrades) * 100 : 0} />  
                  </div>  
                </div>  
              </motion.div>  
            )}  
          </>  
        )}  
      </div>  
    </div>  
  );  
}  
  
/* ────────────────────────────────────────────────── */  
/* Sub-components                                      */  
/* ────────────────────────────────────────────────── */  
  
const KPIStat = ({ label, value, sub }) => (  
  <div>  
    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2">{label}</p>  
    <p className="text-2xl sm:text-3xl font-bold text-white leading-none">{value}</p>  
    {sub && <p className="text-[11px] text-gray-500 mt-2">{sub}</p>}  
  </div>  
);  
  
const ModelRow = ({ asset }) => {  
  const dot = asset.is_active ? "bg-green-400 animate-pulse" : asset.has_credentials ? "bg-gray-600" : "bg-yellow-400";  
  const statusText = asset.is_active ? "Live" : asset.has_credentials ? "Off" : "Setup needed";  
  const statusColor = asset.is_active ? "text-green-400" : asset.has_credentials ? "text-gray-500" : "text-yellow-400";  
  
  return (  
    <li className="flex items-center justify-between px-3 py-2.5 bg-black/30 border border-gray-800 rounded-lg">  
      <div className="flex items-center gap-3 min-w-0">  
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />  
        <div className="min-w-0">  
          <p className="text-sm font-semibold text-white truncate">{asset.display_name}</p>  
          <p className="text-[10px] text-gray-500 font-mono truncate">{asset.symbol}</p>  
        </div>  
      </div>  
      <span className={`text-[11px] font-semibold uppercase tracking-wider ${statusColor}`}>{statusText}</span>  
    </li>  
  );  
};  
  
const TradeRow = ({ trade, onClick }) => {  
  const pnl = trade.pnl || 0;  
  const positive = pnl >= 0;  
  return (  
    <li  
      onClick={onClick}  
      className="flex items-center justify-between px-3 py-2.5 bg-black/30 border border-gray-800 hover:border-gold/30 rounded-lg cursor-pointer transition-all"  
    >  
      <div className="flex items-center gap-3 min-w-0">  
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${  
          trade.side === "BUY" || trade.side === "buy"  
            ? "bg-blue-500/10 text-blue-400 border border-blue-500/40"  
            : "bg-orange-500/10 text-orange-400 border border-orange-500/40"  
        }`}>{trade.side}</span>  
        <div className="min-w-0">  
          <p className="text-sm font-semibold text-white truncate">{trade.symbol}</p>  
          <p className="text-[10px] text-gray-500">  
            {new Date(trade.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}  
          </p>  
        </div>  
      </div>  
      <p className={`text-sm font-bold flex-shrink-0 ${positive ? "text-green-400" : "text-red-400"}`}>  
        {positive ? "+" : "−"}${Math.abs(pnl).toFixed(2)}  
      </p>  
    </li>  
  );  
};  
  
const RatioRow = ({ color, label, value, pct }) => (  
  <div>  
    <div className="flex items-center justify-between mb-1.5">  
      <div className="flex items-center gap-2">  
        <span className={`w-2 h-2 rounded-full ${color}`} />  
        <span className="text-xs text-gray-400">{label}</span>  
      </div>  
      <span className="text-sm font-bold text-white">{value} <span className="text-[10px] font-normal text-gray-500">· {pct.toFixed(1)}%</span></span>  
    </div>  
    <div className="h-1 bg-black/40 rounded-full overflow-hidden">  
      <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, ease: "easeOut" }} className={`h-full ${color}`} />  
    </div>  
  </div>  
);  
  
const EmptyBlock = ({ text }) => (  
  <div className="py-8 text-center">  
    <p className="text-xs text-gray-500">{text}</p>  
  </div>  
);  
  
const OnboardingCard = ({ botMeta, navigate }) => {  
  const steps = [  
    { done: botMeta.hasBinance || botMeta.hasIG, title: "Connect a broker", desc: "Link Binance or IG Markets so the bot can trade.", cta: "Go to Accounts", path: "/accounts" },  
    { done: false, title: "Turn on a model", desc: "Pick a market (BTC, Gold) and start its AI model.", cta: "Go to My Models", path: "/my-models" },  
    { done: false, title: "Watch it work", desc: "Trades will appear in your dashboard and logs.", cta: "See Trade Logs", path: "/trade-logs" },  
  ];  
  return (  
    <motion.div  
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}  
      className="bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60 backdrop-blur-xl border border-gray-800 rounded-2xl p-6 sm:p-8"  
    >  
      <div className="mb-6">  
        <h2 className="text-lg font-bold text-white">Welcome to iTrader</h2>  
        <p className="text-sm text-gray-400 mt-1">Three quick steps and your bot is running.</p>  
      </div>  
      <ol className="space-y-3">  
        {steps.map((s, i) => (  
          <li key={i} className={`flex items-start gap-4 p-4 rounded-xl border ${  
            s.done ? "bg-green-500/5 border-green-500/30" : "bg-black/30 border-gray-800"  
          }`}>  
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border ${  
              s.done ? "bg-green-500/20 border-green-500/50 text-green-400" : "bg-black/50 border-gray-700 text-gray-400"  
            }`}>  
              {s.done ? (  
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />  
                </svg>  
              ) : <span className="text-sm font-bold">{i + 1}</span>}  
            </div>  
            <div className="flex-1 min-w-0">  
              <p className="text-sm font-semibold text-white">{s.title}</p>  
              <p className="text-xs text-gray-400 mt-0.5">{s.desc}</p>  
            </div>  
            {!s.done && (  
              <button  
                onClick={() => navigate(s.path)}  
                className="px-3 py-1.5 text-[11px] font-semibold rounded-lg bg-gold/10 border border-gold/40 text-gold hover:bg-gold/20 transition-all flex-shrink-0"  
              >  
                {s.cta}  
              </button>  
            )}  
          </li>  
        ))}  
      </ol>  
    </motion.div>  
  );  
};  
  
/* ────────────────────────────────────────────────── */  
/* Helpers                                             */  
/* ────────────────────────────────────────────────── */  
const buildEquityCurve = (trades) => {  
  if (!trades.length) return [];  
  const sorted = [...trades].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));  
  const first = sorted[0];  
  let balance = (first.balance || 0) - (first.pnl || 0);  
  return sorted.map((t, i) => {  
    balance += t.pnl || 0;  
    return {  
      date: new Date(t.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" }),  
      balance: Number(balance.toFixed(2)),  
      trade: i + 1,  
    };  
  });  
};  
  
export default Dashboard;  